import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Box,
  Typography,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  LocationOn,
  Description,
  Info,
  QrCode2,
  Home,
  Apartment,
  DomainAdd
} from '@mui/icons-material';

// Configuration des parties communes
const COMMON_AREAS = [
  { value: 'entrance_hall', label: 'Hall d\'entrée' },
  { value: 'garage_entrance', label: 'Portail garage (parking)' },
  { value: 'trash_room', label: 'Local poubelles' },
  { value: 'elevator_1', label: 'Ascenseur 1' },
  { value: 'elevator_2', label: 'Ascenseur 2' },
  { value: 'mailbox_area', label: 'Salle des boîtes aux lettres' },
  { value: 'storage_room', label: 'Débarras collectif' },
  { value: 'gym', label: 'Salle de fitness' },
  { value: 'parking_level_1', label: 'Parking niveau 1' },
  { value: 'parking_level_2', label: 'Parking niveau 2' },
  { value: 'courtyard', label: 'Cour intérieure' },
  { value: 'other', label: 'Autre' }
];

export default function GenerateQRDialog({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    residenceId: '',
    locationType: '', // 'common_area' ou 'apartment'
    commonArea: '', // pour les parties communes
    apartmentId: '', // pour les appartements
    apartmentNumber: '', // numéro d'appartement
    location: '',
    description: ''
  });

  const [residences, setResidences] = useState([]);
  const [apartments, setApartments] = useState([]); // Liste des appartements
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingApartments, setLoadingApartments] = useState(false);

  useEffect(() => {
    if (open) {
      loadResidences();
    }
  }, [open]);

  // Charger les appartements quand la résidence change
  useEffect(() => {
    if (formData.residenceId && formData.locationType === 'apartment') {
      loadApartments();
    }
  }, [formData.residenceId, formData.locationType]);

  const loadResidences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/residences', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setResidences(data.residences || []);
      }
    } catch (e) {
      console.error('Erreur chargement résidences:', e);
    }
  };

  const loadApartments = async () => {
    try {
      setLoadingApartments(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/residences/${formData.residenceId}/tenants`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        // Grouper par numéro d'appartement unique
        const uniqueApartments = Array.from(
          new Map(
            (data.tenants || []).map((t) => [t.apartmentNumber, t])
          ).values()
        );
        setApartments(uniqueApartments);
      }
    } catch (e) {
      console.error('Erreur chargement appartements:', e);
      setError('Erreur lors du chargement des appartements');
    } finally {
      setLoadingApartments(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.residenceId) {
      setError('La résidence est obligatoire');
      return;
    }

    if (!formData.locationType) {
      setError('Le type de localisation est obligatoire');
      return;
    }

    if (formData.locationType === 'common_area' && !formData.commonArea) {
      setError('La partie commune est obligatoire');
      return;
    }

    if (formData.locationType === 'apartment' && !formData.apartmentNumber) {
      setError('L\'appartement est obligatoire');
      return;
    }

    // Construire le location en fonction du type
    let location = '';
    if (formData.locationType === 'common_area') {
      const area = COMMON_AREAS.find((a) => a.value === formData.commonArea);
      location = area ? area.label : formData.commonArea;
    } else if (formData.locationType === 'apartment') {
      location = `Appartement ${formData.apartmentNumber}`;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/qrcodes', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          residenceId: formData.residenceId,
          locationType: formData.locationType,
          location,
          commonArea: formData.commonArea,
          apartmentNumber: formData.locationType === 'apartment' ? formData.apartmentNumber : null,
          description: formData.description
        })
      });

      if (response.ok) {
        const data = await response.json();
        onSuccess(data.qrcode);
        handleClose();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Erreur lors de la création du QR code');
      }
    } catch (e) {
      setError('Erreur de connexion: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      residenceId: '',
      locationType: '',
      commonArea: '',
      apartmentId: '',
      apartmentNumber: '',
      location: '',
      description: ''
    });
    setError('');
    setApartments([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode2 sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Générer un nouveau QR Code</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Résidence - OBLIGATOIRE */}
          <FormControl fullWidth required>
            <InputLabel>Résidence *</InputLabel>
            <Select
              value={formData.residenceId}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  residenceId: e.target.value,
                  locationType: '', // Reset
                  commonArea: '',
                  apartmentNumber: ''
                });
              }}
              label="Résidence *"
              startAdornment={
                <InputAdornment position="start">
                  <Home sx={{ color: 'action.active', mr: 1 }} />
                </InputAdornment>
              }
            >
              {residences.map((res) => (
                <MenuItem key={res.id} value={res.id}>
                  {res.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }} />

          {/* Type de localisation - OBLIGATOIRE */}
          <FormControl fullWidth required>
            <InputLabel>Type de localisation *</InputLabel>
            <Select
              value={formData.locationType}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  locationType: e.target.value,
                  commonArea: '',
                  apartmentNumber: ''
                });
              }}
              label="Type de localisation *"
            >
              <MenuItem value="common_area">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DomainAdd />
                  Partie commune
                </Box>
              </MenuItem>
              <MenuItem value="apartment">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Apartment />
                  Logement
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Sélection conditionnelle - PARTIE COMMUNE */}
          {formData.locationType === 'common_area' && (
            <FormControl fullWidth required>
              <InputLabel>Partie commune *</InputLabel>
              <Select
                value={formData.commonArea}
                onChange={(e) =>
                  setFormData({ ...formData, commonArea: e.target.value })
                }
                label="Partie commune *"
              >
                {COMMON_AREAS.map((area) => (
                  <MenuItem key={area.value} value={area.value}>
                    {area.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Sélection conditionnelle - LOGEMENT */}
          {formData.locationType === 'apartment' && (
            <FormControl fullWidth required>
              <InputLabel>Appartement *</InputLabel>
              <Select
                value={formData.apartmentNumber}
                onChange={(e) =>
                  setFormData({ ...formData, apartmentNumber: e.target.value })
                }
                label="Appartement *"
                disabled={loadingApartments}
              >
                {apartments.length > 0 ? (
                  apartments.map((apt) => (
                    <MenuItem key={apt.id} value={apt.apartmentNumber}>
                      {apt.apartmentNumber}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {loadingApartments ? 'Chargement...' : 'Aucun appartement'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Description - OPTIONNEL */}
          <TextField
            fullWidth
            label="Description (optionnel)"
            placeholder="Description détaillée pour faciliter l'identification..."
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                  <Description sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Message d'aide */}
          <Alert severity="info" icon={<Info />} sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              Un code QR unique sera généré pour ce{' '}
              {formData.locationType === 'common_area'
                ? 'lieu commun'
                : formData.locationType === 'apartment'
                ? 'logement'
                : 'lieu'}.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Les agents pourront le coller et le scanner pour accéder rapidement aux
              informations du{' '}
              {formData.locationType === 'common_area' ? 'lieu' : 'logement'}.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !formData.residenceId ||
            !formData.locationType ||
            (formData.locationType === 'common_area' && !formData.commonArea) ||
            (formData.locationType === 'apartment' && !formData.apartmentNumber)
          }
          startIcon={loading ? null : <QrCode2 />}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Création...' : 'Créer QR Code'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
